import React from 'react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  /** Show compact version on mobile (default: true) */
  compactOnMobile?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  compactOnMobile = true,
}) => {
  // Generate page numbers to display - compact version for mobile
  const getPageNumbers = (compact: boolean = false) => {
    const pages: (number | string)[] = [];
    const maxDisplayedPages = compact ? 3 : 7;

    if (totalPages <= maxDisplayedPages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else if (compact) {
      // Compact: show current page and maybe neighbors
      if (currentPage > 1) pages.push(1);
      if (currentPage > 2) pages.push('...');
      pages.push(currentPage);
      if (currentPage < totalPages - 1) pages.push('...');
      if (currentPage < totalPages) pages.push(totalPages);
    } else {
      // Full version
      pages.push(1);

      if (currentPage <= 3) {
        for (let i = 2; i <= 5; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          if (i > 1) pages.push(i);
        }
      } else {
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  if (totalPages <= 1) return null;

  const desktopPages = getPageNumbers(false);
  const mobilePages = getPageNumbers(true);

  const PageButton: React.FC<{
    page: number | string;
    index: number;
    isMobile?: boolean;
  }> = ({ page, index, isMobile }) => {
    if (page === '...') {
      return (
        <span
          key={`ellipsis-${index}`}
          className={`inline-flex items-center justify-center border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 ${
            isMobile ? 'px-2 py-1.5 text-xs' : 'px-3 py-2 text-sm'
          }`}
        >
          ...
        </span>
      );
    }

    const pageNumber = page as number;
    const isCurrent = pageNumber === currentPage;

    return (
      <button
        key={pageNumber}
        onClick={() => onPageChange(pageNumber)}
        className={`inline-flex items-center justify-center border font-medium rounded-md transition-colors ${
          isMobile ? 'px-3 py-1.5 text-xs min-h-[36px] min-w-[36px]' : 'px-4 py-2 text-sm min-h-[40px]'
        } ${
          isCurrent
            ? 'z-10 bg-blue-50 dark:bg-blue-900/30 border-blue-500 text-blue-600 dark:text-blue-400'
            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 active:bg-gray-100'
        }`}
      >
        {pageNumber}
      </button>
    );
  };

  const NavButton: React.FC<{
    direction: 'prev' | 'next';
    disabled: boolean;
    isMobile?: boolean;
  }> = ({ direction, disabled, isMobile }) => (
    <button
      onClick={() => onPageChange(direction === 'prev' ? currentPage - 1 : currentPage + 1)}
      disabled={disabled}
      className={`inline-flex items-center justify-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
        isMobile ? 'p-2 min-h-[36px] min-w-[36px]' : 'px-3 py-2 min-h-[40px]'
      }`}
      aria-label={direction === 'prev' ? 'Previous page' : 'Next page'}
    >
      <svg className={isMobile ? 'h-4 w-4' : 'h-5 w-5'} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d={direction === 'prev' ? 'M15 19l-7-7 7-7' : 'M9 5l7 7-7 7'}
        />
      </svg>
    </button>
  );

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
      {/* Info text */}
      <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 order-2 sm:order-1">
        <span className="hidden xs:inline">Showing </span>
        <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span>
        <span className="xs:hidden">-</span>
        <span className="hidden xs:inline"> to </span>
        <span className="font-medium">{Math.min(currentPage * pageSize, totalItems)}</span>
        <span className="hidden xs:inline"> of </span>
        <span className="xs:hidden"> / </span>
        <span className="font-medium">{totalItems}</span>
        <span className="hidden xs:inline"> results</span>
      </div>

      {/* Desktop pagination */}
      {compactOnMobile && (
        <nav className="hidden sm:flex items-center gap-1 order-1 sm:order-2">
          <NavButton direction="prev" disabled={currentPage <= 1} />
          {desktopPages.map((page, index) => (
            <PageButton key={`desktop-${index}`} page={page} index={index} />
          ))}
          <NavButton direction="next" disabled={currentPage >= totalPages} />
        </nav>
      )}

      {/* Mobile pagination */}
      {compactOnMobile && (
        <nav className="flex sm:hidden items-center gap-1 order-1">
          <NavButton direction="prev" disabled={currentPage <= 1} isMobile />
          {mobilePages.map((page, index) => (
            <PageButton key={`mobile-${index}`} page={page} index={index} isMobile />
          ))}
          <NavButton direction="next" disabled={currentPage >= totalPages} isMobile />
        </nav>
      )}

      {/* Non-compact mode (same for all sizes) */}
      {!compactOnMobile && (
        <nav className="flex items-center gap-1 order-1 sm:order-2">
          <NavButton direction="prev" disabled={currentPage <= 1} />
          {desktopPages.map((page, index) => (
            <PageButton key={`full-${index}`} page={page} index={index} />
          ))}
          <NavButton direction="next" disabled={currentPage >= totalPages} />
        </nav>
      )}
    </div>
  );
};

export default Pagination;
